<template>
  <OareContentView title="Bibliography">
    <v-row>
      <v-col cols="2">
        <div class="sticky">
          <v-text-field
            v-model="search"
            label="Search"
            single-line
            hide-details
            clearable
            class="test-search"
          />
          <v-radio-group v-model="selectedType" hide-details>
            <template #label>
              <span class="font-weight-bold">Bibliography Style</span>
            </template>
            <v-radio
              v-for="(type, idx) in types"
              :key="idx"
              :label="type.name"
              :value="type.value"
              :class="`test-radio-btn-bibType-${idx}`"
            ></v-radio>
          </v-radio-group>
          <v-radio-group
            v-model="sortBy"
            class="test-radio-group-sortBy"
            hide-details
          >
            <template #label>
              <span class="font-weight-bold">Sort By</span>
            </template>
            <v-radio
              v-for="(item, idx) in sortByItems"
              :key="idx"
              :label="item.text"
              :value="item.value"
            ></v-radio>
          </v-radio-group>
        </div>
      </v-col>
      <v-col cols="8">
        <!-- The body slot renders the rows a chunk per frame (see
             useProgressiveList) as plain markup with the data table's classes,
             and one shared comment menu below serves every row: mounting all
             1,780 items, each with its own components, in one task froze the
             page, app bar menus included, for seconds. -->
        <v-data-table
          :loading="loading"
          :headers="itemsHeaders"
          hide-default-header
          hide-default-footer
        >
          <template #body>
            <tr
              v-if="loading && !filteredBib.length"
              class="v-data-table-rows-loading"
            >
              <td colspan="1">Loading items...</td>
            </tr>
            <tr
              v-else-if="!filteredBib.length"
              class="v-data-table-rows-no-data"
            >
              <td colspan="1">No data available</td>
            </tr>
            <!-- v-memo skips the chunks already rendered; Vue supports it on
                 a <template v-for>, which this lint rule does not know. -->
            <!-- eslint-disable vue/no-useless-template-attributes -->
            <template
              v-for="(chunk, idx) in bibChunks"
              :key="idx"
              v-memo="[chunk, canComment]"
            >
              <!-- eslint-enable vue/no-useless-template-attributes -->
              <tr
                v-for="item in chunk"
                :key="item.uuid"
                class="v-data-table__tr"
              >
                <td class="v-data-table__td v-data-table-column--align-start">
                  <div class="v-row v-row--density-default">
                    <div class="v-col v-col--cols-8">
                      <div
                        class="test-word-util-list"
                        :class="{
                          'cursor-display': canComment,
                        }"
                        @click="openMenu($event, item)"
                      >
                        <span class="test-bib" v-if="!item.bibliography.bib"
                          >No Bibliography Data</span
                        >
                        <span
                          class="test-bib"
                          v-else-if="!item.bibliography.url"
                          v-html="item.bibliography.bib"
                        >
                        </span>
                        <a
                          v-else
                          class="test-bib"
                          :href="item.bibliography.url"
                          target="_blank"
                          v-html="item.bibliography.bib"
                        ></a>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </template>
        </v-data-table>
        <!-- Keyed by item: a fresh menu opens on the newly clicked item while
             the previous one's click-outside handling closes the old menu.
             `target` only positions the menu; an `activator` would add
             Vuetify's own click toggle to the row, which undid openMenu and
             left the menu closed when the same item was clicked again. -->
        <v-menu
          v-if="canComment && menuItem"
          :key="menuItem.uuid"
          v-model="menuOpen"
          :target="menuTarget"
        >
          <v-list>
            <v-list-item @click="commentOnMenuItem" class="test-comment">
              <v-list-item-title>
                <v-icon>mdi-comment</v-icon>
                Comment
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-col>
      <v-col cols="2"
        ><div class="sticky">
          <v-radio-group v-model="showItemType" hide-details>
            <template #label>
              <span class="font-weight-bold">Show Items</span>
            </template>
            <v-radio
              v-for="(item, idx) in itemType"
              :key="idx"
              :label="`${item.charAt(0).toUpperCase()}${item.substring(1)}`"
              :value="item"
              :class="`test-radio-${item}`"
            ></v-radio>
          </v-radio-group></div
      ></v-col>
    </v-row>
    <comment-item-display
      v-if="canComment"
      v-model="isCommenting"
      :item="commentDialogItem"
      :uuid="commentDialogUuid"
      :key="commentDialogUuid"
      route="/bibliographies"
      >{{ commentDialogItem }}</comment-item-display
    >
  </OareContentView>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onMounted,
  ref,
  Ref,
  shallowRef,
  triggerRef,
  watch,
} from 'vue';
import CommentItemDisplay from '@/components/CommentItemDisplay/index.vue';
import useQueryParam from '@/hooks/useQueryParam';
import useProgressiveList from '@/hooks/useProgressiveList';
import sl from '@/serviceLocator';
import { BibliographyResponse } from '@oare/types';
import { DataTableHeader } from 'vuetify';
import _ from 'lodash';

export default defineComponent({
  components: {
    CommentItemDisplay,
  },
  setup() {
    const server = sl.get('serverProxy');
    const actions = sl.get('globalActions');
    const store = sl.get('store');
    const types = ref([
      { name: 'Chicago Author Date', value: 'chicago-author-date' },
      { name: 'Chicago Note Bibliography', value: 'chicago-note-bibliography' },
    ]);
    const selectedType = useQueryParam('type', 'chicago-author-date', true);
    const sortBy = useQueryParam('sortBy', 'title', true);
    const search = useQueryParam('query', '', true);
    const showItemType = useQueryParam('showItemType', 'all', true);
    const itemType: Ref<string[]> = ref(['all']);
    const isCommenting = ref(false);
    const commentDialogUuid = ref('');
    const commentDialogItem = ref('');

    const loading = ref(false);

    const itemsHeaders: Ref<DataTableHeader[]> = ref([
      {
        title: 'Bibliography',
        key: 'bibliography',
        sortable: false,
      },
    ]);
    const sortByItems: Ref<{ text: string; value: string }[]> = ref([
      { text: 'Title', value: 'title' },
      { text: 'Author', value: 'author' },
      { text: 'Date', value: 'date' },
    ]);
    // Shallow: nothing edits an item, and deep proxies on 1,780 nested items
    // only add tracking work to every render.
    const bibliographyResponse = shallowRef<BibliographyResponse[]>([]);
    const filteredBib = shallowRef<BibliographyResponse[]>([]);
    const bibChunks = useProgressiveList(filteredBib);

    onMounted(async () => {
      try {
        loading.value = true;
        await updateBibliography();
      } catch (err) {
        actions.showErrorSnackbar(
          `Error loading bibliography. Please try again.`,
          err as Error
        );
      } finally {
        loading.value = false;
      }
    });

    const canComment = computed(() => store.hasPermission('ADD_COMMENTS'));

    const openComment = (uuid: string, item: BibliographyResponse) => {
      commentDialogUuid.value = uuid;
      commentDialogItem.value = item.title ? item.title : 'Title not available';
      isCommenting.value = true;
    };

    const menuOpen = ref(false);
    const menuTarget = ref<Element>();
    const menuItem = ref<BibliographyResponse>();

    const openMenu = (event: MouseEvent, item: BibliographyResponse) => {
      if (!canComment.value) {
        return;
      }
      menuTarget.value = event.currentTarget as Element;
      menuItem.value = item;
      menuOpen.value = true;
    };

    const commentOnMenuItem = () => {
      if (menuItem.value) {
        openComment(menuItem.value.uuid, menuItem.value);
      }
    };

    const updateBibliography = async () => {
      bibliographyResponse.value = [];
      filteredBib.value = [];
      itemType.value = ['all'];
      showItemType.value = 'all';
      bibliographyResponse.value = await server.getBibliographies({
        citationStyle: selectedType.value,
      });
      bibliographyResponse.value.forEach(bib => {
        const bibItemTypeClean = bib.itemType?.split(/(?=[A-Z])/g).join(' ');
        if (bibItemTypeClean && !itemType.value.includes(bibItemTypeClean)) {
          itemType.value.push(bibItemTypeClean);
        }
      });
      filteredBib.value = bibliographyResponse.value;
      sortBibliography();
    };

    const filterBibliography = () => {
      filteredBib.value = bibliographyResponse.value.filter(bib => {
        return bib.bibliography?.bib
          ?.replace(/<.+?>/g, '')
          .toLocaleLowerCase()
          .includes(search.value.toLocaleLowerCase());
      });
      sortBibliography();
    };

    // Compares exactly as String.prototype.localeCompare does, without
    // building a collator on every call.
    const collator = new Intl.Collator();

    const sortBibliography = () => {
      if (sortBy.value === 'title') {
        // Split each title into words once rather than in every comparison.
        const titleWords = new Map(
          filteredBib.value.map(bib => [
            bib,
            bib.title?.match(
              /([^!‘“”`@#$%^&*()’_+\-=\[\]{};':"\\|,.<>\/?\s\d])+/g
            ) ?? [],
          ])
        );
        filteredBib.value.sort((a, b) => {
          if (a.title && b.title) {
            const articles = ['a', 'an', 'the', 'à'];
            const aTitle = titleWords.get(a) ?? [];
            const bTitle = titleWords.get(b) ?? [];
            const minLen =
              aTitle.length <= bTitle.length ? aTitle.length : bTitle.length;
            for (let i = 0; i < minLen; i += 1) {
              const aComparison =
                articles.includes(aTitle[i].toLocaleLowerCase()) &&
                i === 0 &&
                aTitle[i + 1]
                  ? aTitle[i + 1]
                  : aTitle[i];
              const bComparison =
                articles.includes(bTitle[i].toLocaleLowerCase()) &&
                i === 0 &&
                bTitle[i + 1]
                  ? bTitle[i + 1]
                  : bTitle[i];
              if (aComparison !== bComparison) {
                return collator.compare(aComparison, bComparison);
              }
            }
            return aTitle.length - bTitle.length;
          } else if (!a.title && b.title) {
            return -1;
          }
          return 1;
        });
      }
      if (sortBy.value === 'author') {
        filteredBib.value.sort((a, b) => {
          if (a.authors.length > 0 && b.authors.length > 0) {
            const minLen =
              a.authors.length <= b.authors.length
                ? a.authors.length
                : b.authors.length;
            for (let i = 0; i < minLen; i += 1) {
              const aAuthorFullName = a.authors[i].split(' ');
              const bAuthorFullName = b.authors[i].split(' ');
              const aAuthorLastName =
                aAuthorFullName[aAuthorFullName.length - 1];
              const bAuthorLastName =
                bAuthorFullName[bAuthorFullName.length - 1];
              if (aAuthorLastName !== bAuthorLastName) {
                return collator.compare(aAuthorLastName, bAuthorLastName);
              }
            }
            return a.authors.length - b.authors.length;
          } else if (a.authors.length === 0 && b.authors.length > 0) {
            return 1;
          }
          return -1;
        });
      }
      if (sortBy.value === 'date') {
        filteredBib.value.sort((a, b) => {
          let aDate: number | null = null;
          let bDate: number | null = null;
          if (a.date && b.date) {
            aDate = Number(/[0-9]{4}/.exec(a.date));
            bDate = Number(/[0-9]{4}/.exec(b.date));
            if (aDate && bDate) {
              return aDate - bDate;
            } else if (!aDate && bDate) {
              return 1;
            }
            return -1;
          } else if (!a.date && b.date) {
            return 1;
          }
          return -1;
        });
      }
      // The sorts work in place, which a shallow ref does not see.
      triggerRef(filteredBib);
    };

    watch(search, _.debounce(filterBibliography, 100), {
      immediate: false,
    });

    watch(sortBy, sortBibliography);

    watch(showItemType, filterBibliography);

    watch(
      [selectedType],
      async () => {
        try {
          loading.value = true;
          await updateBibliography();
        } catch (err) {
          actions.showErrorSnackbar(
            `Error updating bibliography. Please try again.`,
            err as Error
          );
        } finally {
          loading.value = false;
        }
      },
      { deep: true }
    );

    return {
      server,
      types,
      selectedType,
      itemsHeaders,
      filteredBib,
      bibChunks,
      loading,
      search,
      sortBy,
      sortByItems,
      itemType,
      showItemType,
      isCommenting,
      commentDialogUuid,
      commentDialogItem,
      canComment,
      menuOpen,
      menuTarget,
      menuItem,
      openMenu,
      commentOnMenuItem,
    };
  },
});
</script>
<style>
.sticky {
  position: sticky;
  top: 1.2in;
}
.cursor-display {
  cursor: pointer;
}
</style>
